import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import fnc from '../../mymod/commFunction';
import './LoginHistory.scss';

const TAB = {
    LOGIN: 'login',
    MENU: 'menu',
    USAGE: 'usage',
    WALLET: 'wallet'
};

function formatCell(value) {
    if (value === null || value === undefined || value === 'NULL') return '';
    return String(value);
}

function formatCost4(value) {
    if (value === null || value === undefined || value === '' || value === 'NULL') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return formatCell(value);
    return num.toFixed(4);
}

const LoginHistory = () => {
    const location = useLocation();
    const pageTitle = location.state?.title || '회원 로그';

    const [tab, setTab] = useState(TAB.LOGIN);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

    const tabLabel = useMemo(() => {
        if (tab === TAB.LOGIN) return '로그인 히스토리';
        if (tab === TAB.MENU) return '메뉴클릭 히스토리';
        if (tab === TAB.USAGE) return '사용 히스토리';
        return '지갑 거래 히스토리';
    }, [tab]);

    useEffect(() => {
        if (tab === TAB.USAGE || tab === TAB.WALLET) {
            setSortConfig({ key: 'created_at', direction: 'desc' });
        } else {
            setSortConfig({ key: '', direction: 'asc' });
        }
        fetchHistory();
    }, [tab]);

    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return rows;

        const numericFields = new Set([
            'sno',
            'id',
            'amount',
            'balance_after',
            'pageCnt',
            'total_page',
            'input_cost_usd',
            'output_cost_usd',
            'total_cost_usd',
            'total_cost_krw'
        ]);

        const toSortValue = (item, key) => {
            const raw = item?.[key];
            if (raw === null || raw === undefined || raw === '') return null;

            if (numericFields.has(key)) {
                const n = Number(raw);
                return Number.isNaN(n) ? null : n;
            }

            if (key === 'created_at') {
                const ts = Date.parse(String(raw));
                return Number.isNaN(ts) ? String(raw).toLowerCase() : ts;
            }

            return String(raw).toLowerCase();
        };

        return [...rows].sort((a, b) => {
            const av = toSortValue(a, sortConfig.key);
            const bv = toSortValue(b, sortConfig.key);

            if (av === null && bv === null) return 0;
            if (av === null) return 1;
            if (bv === null) return -1;

            let result = 0;
            if (typeof av === 'number' && typeof bv === 'number') {
                result = av - bv;
            } else {
                result = String(av).localeCompare(String(bv), 'ko');
            }

            return sortConfig.direction === 'asc' ? result : -result;
        });
    }, [rows, sortConfig]);

    function handleRefresh() {
        fetchHistory();
    }

    function fetchHistory() {
        setLoading(true);

        if (tab === TAB.LOGIN) {
            fnc.sendAxios('/userRoutes/selectLoginHistory', {
                rowLimit: 1000
            }, (res) => {
                setRows(Array.isArray(res) ? res : []);
                setLoading(false);
            });
            return;
        }

        if (tab === TAB.MENU) {
            fnc.sendAxios('/userRoutes/selectMenuClickHistory', {
                rowLimit: 1000
            }, (res) => {
                setRows(Array.isArray(res) ? res : []);
                setLoading(false);
            });
            return;
        }

        if (tab === TAB.USAGE) {
            fnc.sendAxios('/userRoutes/selectAiTokenUsageHistory', {
                rowLimit: 1000
            }, (res) => {
                setRows(Array.isArray(res) ? res : []);
                setLoading(false);
            });
            return;
        }

        fnc.sendAxios('/userRoutes/selectWalletTransactionHistory', {
            rowLimit: 1000
        }, (res) => {
            setRows(Array.isArray(res) ? res : []);
            setLoading(false);
        });
    }

    const colSpan = tab === TAB.LOGIN
        ? 12
        : tab === TAB.MENU
            ? 11
            : tab === TAB.USAGE
                ? 11
                : 13;

    function refreshButtonText() {
        if (loading) return '새로고침 중...';
        return '새로고침';
    }

    function handleSort(key) {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                };
            }

            return {
                key,
                direction: 'asc'
            };
        });
    }

    function renderHeader(label, key) {
        const indicator = sortConfig.key === key
            ? sortConfig.direction === 'asc'
                ? ' (asc)'
                : ' (desc)'
            : '';

        return (
            <th
                onClick={() => handleSort(key)}
                style={{ cursor: 'pointer' }}
                title="클릭해서 정렬"
            >
                {label}
                {indicator}
            </th>
        );
    }

    function buildRowKey(prefix, item, index) {
        const primaryId = item?.sno ?? item?.id;
        if (primaryId !== null && primaryId !== undefined && primaryId !== '') {
            return `${prefix}-${primaryId}-${index}`;
        }

        const userId = item?.user_id ?? '';
        const stCode = item?.stCode ?? '';
        const createdAt = item?.created_at ?? '';
        return `${prefix}-${userId}-${stCode}-${createdAt}-${index}`;
    }

    return (
        <div className="LoginHistory">
            <div className="login-history-title-wrap">
                <h2>{pageTitle}</h2>
            </div>

            <div className="history-tab-buttons">
                <button
                    type="button"
                    className={tab === TAB.LOGIN ? 'active' : ''}
                    onClick={() => setTab(TAB.LOGIN)}
                >
                    로그인
                </button>
                <button
                    type="button"
                    className={tab === TAB.MENU ? 'active' : ''}
                    onClick={() => setTab(TAB.MENU)}
                >
                    메뉴클릭
                </button>
                <button
                    type="button"
                    className={tab === TAB.USAGE ? 'active' : ''}
                    onClick={() => setTab(TAB.USAGE)}
                >
                    사용
                </button>
                <button
                    type="button"
                    className={tab === TAB.WALLET ? 'active' : ''}
                    onClick={() => setTab(TAB.WALLET)}
                >
                    지갑 거래
                </button>
                <button
                    type="button"
                    className="btn-refresh"
                    onClick={handleRefresh}
                    disabled={loading}
                >
                    {refreshButtonText()}
                </button>
            </div>

            <div className="history-caption">
                <strong>{tabLabel}</strong>
                <span>({rows.length}건)</span>
            </div>

            <div className="history-table-wrap">
                <table className="history-table">
                    <thead>
                        {tab === TAB.LOGIN ? (
                            <tr>
                                {renderHeader('sno', 'sno')}
                                {renderHeader('created_at', 'created_at')}
                                {renderHeader('user_id', 'user_id')}
                                {renderHeader('stCode', 'stCode')}
                                {renderHeader('이름', 'user_name')}
                                {renderHeader('login_type', 'login_type')}
                                {renderHeader('success_yn', 'success_yn')}
                                {renderHeader('fail_reason', 'fail_reason')}
                                {renderHeader('app_name', 'app_name')}
                                {renderHeader('machine_name', 'machine_name')}
                                {renderHeader('os_user', 'os_user')}
                                {renderHeader('os_version', 'os_version')}
                            </tr>
                        ) : tab === TAB.MENU ? (
                            <tr>
                                {renderHeader('sno', 'sno')}
                                {renderHeader('created_at', 'created_at')}
                                {renderHeader('user_id', 'user_id')}
                                {renderHeader('stCode', 'stCode')}
                                {renderHeader('이름', 'user_name')}
                                {renderHeader('menu_name', 'menu_name')}
                                {renderHeader('menu_source', 'menu_source')}
                                {renderHeader('app_name', 'app_name')}
                                {renderHeader('machine_name', 'machine_name')}
                                {renderHeader('os_user', 'os_user')}
                                {renderHeader('os_version', 'os_version')}
                            </tr>
                        ) : tab === TAB.USAGE ? (
                            <tr>
                                {renderHeader('created_at', 'created_at')}
                                {renderHeader('user_id', 'user_id')}
                                {renderHeader('stCode', 'stCode')}
                                {renderHeader('이름', 'user_name')}
                                {renderHeader('service_name', 'service_name')}
                                {renderHeader('model_name', 'model_name')}
                                {renderHeader('success_yn', 'success_yn')}
                                {renderHeader('input_cost_usd', 'input_cost_usd')}
                                {renderHeader('output_cost_usd', 'output_cost_usd')}
                                {renderHeader('total_cost_usd', 'total_cost_usd')}
                                {renderHeader('total_cost_krw', 'total_cost_krw')}
                            </tr>
                        ) : (
                            <tr>
                                {renderHeader('id', 'id')}
                                {renderHeader('created_at', 'created_at')}
                                {renderHeader('user_id', 'user_id')}
                                {renderHeader('stCode', 'stCode')}
                                {renderHeader('이름', 'user_name')}
                                {renderHeader('ref_type', 'ref_type')}
                                {renderHeader('amount', 'amount')}
                                {renderHeader('balance_after', 'balance_after')}
                                {renderHeader('ref_id', 'ref_id')}
                                {renderHeader('fileName', 'fileName')}
                                {renderHeader('trans_page', 'trans_page')}
                                {renderHeader('pageCnt', 'pageCnt')}
                                {renderHeader('total_page', 'total_page')}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={colSpan} className="empty">조회 중...</td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={colSpan} className="empty">검색 결과가 없습니다.</td>
                            </tr>
                        ) : tab === TAB.LOGIN ? (
                            sortedRows.map((item, index) => (
                                <tr key={buildRowKey('login', item, index)}>
                                    <td>{formatCell(item.sno)}</td>
                                    <td>{formatCell(item.created_at)}</td>
                                    <td>{formatCell(item.user_id)}</td>
                                    <td>{formatCell(item.stCode)}</td>
                                    <td>{formatCell(item.user_name)}</td>
                                    <td>{formatCell(item.login_type)}</td>
                                    <td>{formatCell(item.success_yn)}</td>
                                    <td>{formatCell(item.fail_reason)}</td>
                                    <td>{formatCell(item.app_name)}</td>
                                    <td>{formatCell(item.machine_name)}</td>
                                    <td>{formatCell(item.os_user)}</td>
                                    <td>{formatCell(item.os_version)}</td>
                                </tr>
                            ))
                        ) : tab === TAB.MENU ? (
                            sortedRows.map((item, index) => (
                                <tr key={buildRowKey('menu', item, index)}>
                                    <td>{formatCell(item.sno)}</td>
                                    <td>{formatCell(item.created_at)}</td>
                                    <td>{formatCell(item.user_id)}</td>
                                    <td>{formatCell(item.stCode)}</td>
                                    <td className='td-user-name'>{formatCell(item.user_name)}</td>
                                    <td className='td-menu-name'>{formatCell(item.menu_name)}</td>
                                    <td>{formatCell(item.menu_source)}</td>
                                    <td>{formatCell(item.app_name)}</td>
                                    <td>{formatCell(item.machine_name)}</td>
                                    <td>{formatCell(item.os_user)}</td>
                                    <td>{formatCell(item.os_version)}</td>
                                </tr>
                            ))
                        ) : tab === TAB.USAGE ? (
                            sortedRows.map((item, index) => (
                                <tr key={buildRowKey('usage', item, index)}>
                                    <td>{formatCell(item.created_at)}</td>
                                    <td>{formatCell(item.user_id)}</td>
                                    <td>{formatCell(item.stCode)}</td>
                                    <td className='td-user-name'>{formatCell(item.user_name)}</td>
                                    <td className='td-service-name'>{formatCell(item.service_name)}</td>
                                    <td>{formatCell(item.model_name)}</td>
                                    <td>{formatCell(item.success_yn)}</td>
                                    <td>{formatCost4(item.input_cost_usd)}</td>
                                    <td>{formatCost4(item.output_cost_usd)}</td>
                                    <td>{formatCost4(item.total_cost_usd)}</td>
                                    <td>{formatCost4(item.total_cost_krw)}</td>
                                </tr>
                            ))
                        ) : (
                            sortedRows.map((item, index) => (
                                <tr key={buildRowKey('wallet', item, index)}>
                                    <td>{formatCell(item.id)}</td>
                                    <td>{formatCell(item.created_at)}</td>
                                    <td>{formatCell(item.user_id)}</td>
                                    <td>{formatCell(item.stCode)}</td>
                                    <td className='td-user-name'>{formatCell(item.user_name)}</td>
                                    <td className='td-ref-type'>{formatCell(item.ref_type)}</td>
                                    <td>{formatCell(item.amount)}</td>
                                    <td>{formatCell(item.balance_after)}</td>
                                    <td>{formatCell(item.ref_id)}</td>
                                    <td>{formatCell(item.fileName)}</td>
                                    <td>{formatCell(item.trans_page)}</td>
                                    <td>{formatCell(item.pageCnt)}</td>
                                    <td>{formatCell(item.total_page)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LoginHistory;
